"use client";

/* eslint-disable react-hooks/purity */
import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { CalendarDays, CheckCircle2, ChevronDown, ChevronUp, ExternalLink, Eye, EyeOff, Receipt, Ticket, Timer, User, Check, Copy, AlertCircle, Download } from "lucide-react";
import { sanitizeLessonHtml } from "@growwithai/shared";
import BrandLogo from "@/components/tenant/BrandLogo";
import { useTenant } from "@/components/tenant/TenantProvider";
import { formatRupiah } from "@/lib/utils";
import { checkoutApi, funnelsApi } from "@/lib/api-client";
import { FunnelBumpCard, type BumpOffer } from "@/components/checkout/FunnelBumpCard";
import { useTurnstile, isTurnstileError } from "@/hooks/use-turnstile";
import { AvatarStack, ParticipantPreview } from "@/components/ui/AvatarStack";
import { track } from "@/lib/tracking/client";
import { pathSegments } from "@/lib/path-slug";
import { inputClass, inputClassReadOnly as readOnlyInputClass } from "@/lib/form-classes";
interface Webinar {
  id: string;
  slug: string;
  title: string;
  subtitle?: string | null;
  body_html?: string | null;
  thumbnail_url?: string | null;
  // Override redirect admin (migration 1019) — path internal atau domain
  // sendiri. Kosong/null = perilaku default (tetap di halaman ini).
  redirect_url?: string | null;
  host_name?: string | null;
  starts_at: string;
  ends_at?: string | null;
  timezone?: string | null;
  capacity?: number | null;
  registered_count?: number;
  participants_preview?: ParticipantPreview[];
  join_url?: string | null;
  calendar_url?: string | null;
  recording_url?: string | null;
  recording_embed_url?: string | null;
  replay_status?: string | null;
  is_registered?: boolean;
  registration?: {
    coupon_code?: string;
    registered_at?: string;
    status?: string;
    registration_type?: string;
    share_proof_count?: number;
  } | null;
  paid_registration_enabled?: number;
  free_registration_enabled?: number;
  paid_registration_price?: number;
  original_price?: number | null;
  share_required_count?: number;
  obligations?: WebinarObligation[];
}
interface WebinarObligation {
  id: string;
  type: string;
  label: string;
  action_url?: string | null;
  requires_proof: number;
  proof_count_required?: number | null;
  completed?: boolean;
  proof_count?: number;
}
interface UserInfo {
  name: string;
  email: string;
  phone: string | null;
}
interface TimeRemaining {
  total: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}
function formatEventDate(date: string, timezone = "Asia/Jakarta") {
  return new Intl.DateTimeFormat("id-ID", {
    timeZone: timezone,
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(date));
}
function setAffiliateCookie(ref: string) {
  const encoded = encodeURIComponent(ref);
  document.cookie = `affiliate_code=${encoded}; path=/; max-age=${30 * 24 * 60 * 60}; SameSite=Lax`;
}
function getTimeRemaining(targetDate: string): TimeRemaining {
  const total = Math.max(0, new Date(targetDate).getTime() - Date.now());
  return {
    total,
    days: Math.floor(total / (1000 * 60 * 60 * 24)),
    hours: Math.floor(total / (1000 * 60 * 60) % 24),
    minutes: Math.floor(total / (1000 * 60) % 60),
    seconds: Math.floor(total / 1000 % 60)
  };
}

// N kartu upload terpisah dan berdiri sendiri (bukan grid tile di dalam 1 box
// bersama) — kartu ke-i dianggap terisi kalau i < uploadedCount (upload
// diasumsikan berurutan; backend cuma menyimpan flat count per registrasi,
// tidak ada kolom index/slot). Ditumpuk penuh (bukan grid 2 kolom) supaya tiap
// bukti jelas terlihat sebagai box terpisah, termasuk statusnya kalau belum
// diunggah.
function ProofUploadSlots(t0) {
  const $ = _c(11);
  const {
    required,
    uploadedCount,
    busy,
    onUpload,
    itemLabel
  } = t0;
  let t1;
  if ($[0] !== busy || $[1] !== itemLabel || $[2] !== onUpload || $[3] !== required || $[4] !== uploadedCount) {
    let t2;
    if ($[6] !== busy || $[7] !== itemLabel || $[8] !== onUpload || $[9] !== uploadedCount) {
      t2 = (_, i) => {
        const filled = i < uploadedCount;
        const isActiveUpload = !filled && busy && i === uploadedCount;
        return <div key={i} className={`rounded-lg border p-3 text-xs ${filled ? "border-green-200 bg-green-50" : isActiveUpload ? "border-primary/30 bg-primary/5" : "border-black/[0.08] bg-white"}`}><div className="mb-1 flex items-center justify-between"><span className="font-medium text-warm-black">{itemLabel} {i + 1}</span>{filled ? <span className="flex items-center gap-1 font-semibold text-green-700"><CheckCircle2 className="h-3.5 w-3.5" /> Terunggah</span> : isActiveUpload ? <span className="flex items-center gap-1 font-semibold text-primary"><svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Mengunggah...</span> : <span className="text-[10px] font-semibold uppercase tracking-wide text-[rgba(12,12,10,0.4)]">Belum diunggah</span>}</div>{!filled && !isActiveUpload && <input type="file" accept="image/*" disabled={busy} onChange={e => {
            const file = e.target.files?.[0];
            if (file) {
              onUpload(file);
            }
            e.target.value = "";
          }} className="block w-full text-[11px] disabled:opacity-50" />}{isActiveUpload && <p className="text-[11px] text-primary/80">{"Memproses gambar & mengunggah, mohon tunggu..."}</p>}</div>;
      };
      $[6] = busy;
      $[7] = itemLabel;
      $[8] = onUpload;
      $[9] = uploadedCount;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    t1 = <div className="space-y-2">{Array.from({
        length: required
      }, t2)}</div>;
    $[0] = busy;
    $[1] = itemLabel;
    $[2] = onUpload;
    $[3] = required;
    $[4] = uploadedCount;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
export default function WebinarRegistrationPage() {
  const {
    brand_name
  } = useTenant();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const slug = pathSegments(pathname)[1] ?? "_";
  const [webinar, setWebinar] = useState<Webinar | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Pernyataan bukti asli + siap diblacklist kalau mengelabui — wajib dicentang
  // sebelum submit final, divalidasi lagi di server (bukan cuma di sini).
  const [integrityConfirmed, setIntegrityConfirmed] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [mobile, setMobile] = useState("");
  const [idempotencyKey] = useState(() => typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2));
  const [authFormMode, setAuthFormMode] = useState<"register" | "login">("register");
  const [guestForm, setGuestForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    referral_source: "",
    _hp_website: ""
  });
  const [loginForm, setLoginForm] = useState({
    email: "",
    password: ""
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [duplicateField, setDuplicateField] = useState<"email" | "phone" | null>(null);
  const turnstile = useTurnstile("turnstile-spin-v2");
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining | null>(null);

  // Gate states
  // selectedMethod = pilihan di Page 1 — MURNI state UI lokal, tidak pernah
  // memicu fetch apa pun saat diubah. Eksekusi (select-method/checkout) hanya
  // terjadi lewat handleContinue (tombol "Lanjutkan").
  const [selectedMethod, setSelectedMethod] = useState<"free_share" | "paid" | null>(null);
  // shareView = Page 2 (jalur gratis) — tampilan penuh menggantikan Page 1,
  // bukan modal. Di-set true oleh handleSelectFreeShare setelah select-method
  // sukses, atau diseed ulang saat reload lewat useEffect di bawah.
  const [shareView, setShareView] = useState(false);
  const [webinarBump, setWebinarBump] = useState<BumpOffer | null>(null);
  const [webinarBumpSelected, setWebinarBumpSelected] = useState(false);
  const [shareData, setShareData] = useState<{
    message: string;
    short_url: string;
  } | null>(null);
  const [obligationBusy, setObligationBusy] = useState<string | null>(null);
  const [shareProofBusy, setShareProofBusy] = useState(false);
  const [shareProofError, setShareProofError] = useState("");
  const [obligationErrors, setObligationErrors] = useState<Record<string, string>>({});
  const [downloadingFlyer, setDownloadingFlyer] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  // Konten "Tentang Webinar" (body_html) collapsed by default — ditaruh di
  // bawah widget harga/CTA, bukan di atas, supaya konten panjang tidak
  // mendorong form pendaftaran jauh ke bawah.
  const [contentExpanded, setContentExpanded] = useState(false);
  const refCode = useMemo(() => searchParams.get("ref") || "", [searchParams]);
  const clickIdParam = useMemo(() => searchParams.get("click_id") || "", [searchParams]);
  const hasStarted = webinar ? new Date(webinar.starts_at).getTime() <= Date.now() : false;
  // Mode "Bayar Saja": berbayar aktif tapi jalur gratis dimatikan admin — tidak
  // ada pilihan nyata untuk ditampilkan, jadi alur registrasi langsung ke bayar.
  const isPaidOnly = Boolean(webinar?.paid_registration_enabled) && !webinar?.free_registration_enabled;
  // Jalur 2-step (gratis via share ATAU bayar) — keduanya tetap bisa diklik kapan
  // saja, tidak dikunci ke pilihan pertama (lihat handleSelectFreeShare/handleSelectPaid).
  const isTwoStep = Boolean(webinar?.paid_registration_enabled) && Boolean(webinar?.free_registration_enabled) && !isPaidOnly;
  // Kewajiban tambahan (opsional, admin-configurable) — webinar tanpa baris
  // obligation (mayoritas) selalu lolos di sini (array kosong/undefined).
  const allObligationsDone = !webinar?.obligations?.length || webinar.obligations.every(o => o.completed);
  // Bukti share DASAR (checklist "share ke N grup" bawaan) — wajib upload asli,
  // bukan cuma centang. Dihitung terhadap share_required_count webinar.
  const shareProofRequired = webinar?.share_required_count || 2;
  const shareProofCount = webinar?.registration?.share_proof_count || 0;
  const shareProofDone = shareProofCount >= shareProofRequired;
  // Alasan konkret kenapa tombol "Selesaikan Pendaftaran Gratis" masih disabled —
  // ditampilkan ke user, bukan cuma tombol abu-abu tanpa penjelasan.
  const pendingFreeRegistrationReasons = [!shareProofDone ? `Unggah ${shareProofRequired - shareProofCount} bukti share lagi` : null, ...(webinar?.obligations || []).filter(o => !o.completed).map(o => `Selesaikan: ${o.label}`)].filter((reason): reason is string => Boolean(reason));
  // Harga coret opsional dari admin tenant — hanya tampil kalau lebih besar dari
  // harga registrasi sebenarnya (kalau tidak, "diskon"-nya negatif/tidak masuk akal).
  const hasOriginalPrice = Boolean(webinar?.original_price && webinar.original_price > (webinar?.paid_registration_price || 0));
  // Checkout webinar berbayar mewajibkan nomor WhatsApp (lihat orders/service.ts) —
  // user login tanpa nomor tersimpan butuh input tambahan sebelum bisa bayar.
  const needsMobile = isLoggedIn === true && !user?.phone && Boolean(webinar?.paid_registration_enabled);

  // Registration is complete if status is registered, registered_free, or registered_paid
  const isCompleted = Boolean(webinar?.is_registered && ['registered', 'registered_free', 'registered_paid'].includes(webinar.registration?.status || '')) || Boolean(successMessage);

  // Sudah selesai jalur gratis (share) tapi belum pernah bayar — masih boleh
  // upgrade ke berbayar, bukan status terkunci seperti registered_paid.
  const canUpgradeToPaid = Boolean(isTwoStep && webinar?.is_registered && webinar.registration?.status === 'registered_free' && !hasStarted);

  // First-party tracking: webinar landing view (once per loaded webinar)
  const webinarId = webinar?.id;
  useEffect(() => {
    if (!webinarId) return;
    track("webinar_viewed", {
      webinar_id: webinarId
    });
  }, [webinarId]);

  // Bump funnel untuk pemilih metode inline — dulu di-fetch lazy saat modal
  // dibuka; sekarang chooser selalu tampil (tanpa modal), jadi fetch begitu
  // syaratnya terpenuhi.
  useEffect(() => {
    if (isLoggedIn && isTwoStep && webinarId && !webinarBump) {
      funnelsApi.getBump('webinar_registration', webinarId).then(res => {
        if (res.success && res.data) setWebinarBump(res.data as BumpOffer);
      });
    }
  }, [isLoggedIn, isTwoStep, webinarId, webinarBump]);
  useEffect(() => {
    if (!refCode) return;
    setAffiliateCookie(refCode);
    fetch("/api/affiliate/track-click", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        affiliate_code: refCode,
        landing_url: window.location.href,
        referrer_url: document.referrer || undefined,
        campaign: `webinar:${slug}`
      })
    }).catch(() => {});
  }, [refCode, slug]);
  useEffect(() => {
    if (clickIdParam) {
      document.cookie = `webinar_click_id=${encodeURIComponent(clickIdParam)}; path=/; max-age=${30 * 24 * 60 * 60}; SameSite=Lax`;
    }
  }, [clickIdParam]);
  const loadWebinar = async () => {
    if (!slug || slug === "_") {
      setIsLoggedIn(false);
      setLoading(false);
      return;
    }
    setError("");

    // Use relative URLs so requests go through the Cloudflare Pages proxy
    // (same-origin), which forwards cookies correctly.
    // This matches the pattern used by the order page.
    const auth = await fetch("/api/auth/me", {
      credentials: "include"
    }).then(r => r.json()).catch(() => ({
      success: false
    }));
    if (auth.success) {
      const currentUser = auth.data?.user || auth.data;
      setIsLoggedIn(true);
      setUser({
        name: currentUser.name || "",
        email: currentUser.email || "",
        phone: currentUser.phone || null
      });

      // Logged-in: use user endpoint for registration status
      const detail = await fetch(`/api/user/webinars/${slug}`, {
        credentials: "include"
      }).then(r => r.json()).catch(() => ({
        success: false
      }));
      if (detail.success) {
        setWebinar(detail.data as Webinar);
      } else {
        // Fallback to public endpoint if user endpoint fails
        const pub = await fetch(`/api/public/webinars/${slug}`, {
          credentials: "include"
        }).then(r => r.json()).catch(() => ({
          success: false
        }));
        if (pub.success) {
          setWebinar(pub.data as Webinar);
        } else {
          setError(pub.error || "Webinar tidak ditemukan.");
        }
      }
    } else {
      setIsLoggedIn(false);
      // Not logged in: use public endpoint
      const detail = await fetch(`/api/public/webinars/${slug}`, {
        credentials: "include"
      }).then(r => r.json()).catch(() => ({
        success: false
      }));
      if (detail.success) {
        setWebinar(detail.data as Webinar);
      } else {
        setError(detail.error || "Webinar tidak ditemukan.");
      }
    }
  };
  useEffect(() => {
    setLoading(true);
    loadWebinar().finally(() => setLoading(false));
  }, [slug]);

  // Resume mid-flow setelah reload: kalau sudah share_confirmation_pending,
  // langsung tampilkan Page 2 (refetch pesan share via handleSelectFreeShare,
  // yang murni membaca ulang — TIDAK mengubah pilihan). payment_pending SENGAJA
  // tidak auto-redirect ke checkout — user harus klik Lanjutkan lagi (tidak ada
  // eksekusi otomatis tanpa aksi eksplisit).
  //
  // Dependency SENGAJA cuma [webinarId], bukan status — supaya efek ini cuma
  // jalan SEKALI saat webinar pertama kali termuat (resume dari reload), bukan
  // setiap kali status berubah jadi share_confirmation_pending (termasuk akibat
  // handleSelectFreeShare sendiri, yang akan bikin efek ini panggil ulang
  // dirinya sendiri berkali-kali — sudah kejadian & diverifikasi via Playwright).
  useEffect(() => {
    if (!webinarId) return;
    if (webinar?.registration?.status === 'share_confirmation_pending') {
      handleSelectFreeShare();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webinarId]);

  // Seed pilihan di Page 1 dari registration_type server (resume pendaftaran
  // yang sudah ada). Registrasi akun baru sekarang tidak pernah pindah halaman
  // (skip-OTP), jadi selectedMethod yang sudah dipilih di state tetap utuh —
  // tidak perlu fallback sessionStorage lagi.
  useEffect(() => {
    if (!webinarId) return;
    const type = webinar?.registration?.registration_type;
    if (type === 'free_share' || type === 'paid') {
      setSelectedMethod(type);
    }
  }, [webinarId, webinar?.registration?.registration_type]);
  useEffect(() => {
    if (!webinar?.starts_at) return;
    const updateCountdown = () => setTimeRemaining(getTimeRemaining(webinar.starts_at));
    updateCountdown();
    const interval = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(interval);
  }, [webinar?.starts_at]);
  const handleSelectFreeShare = async () => {
    if (!webinar) return;
    setSubmitting(true);
    setError("");
    const res = await fetch(`/api/user/webinars/${webinar.id}/select-method`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        method: 'free_share'
      })
    }).then(r => r.json()).catch(() => ({
      success: false
    }));
    setSubmitting(false);
    if (res.success) {
      setShareData(res.data?.share_data || null);
      setShareView(true);
      if (res.data?.registration) {
        setWebinar(prev => prev ? {
          ...prev,
          registration: res.data.registration
        } : null);
      }
    } else {
      setError(res.error || 'Gagal memilih pendaftaran gratis.');
    }
  };
  const handleSelectPaid = async () => {
    if (!webinar) return;
    if (needsMobile && !mobile.trim()) {
      setError("Nomor WhatsApp wajib diisi untuk melanjutkan pembayaran.");
      return;
    }
    setSubmitting(true);
    setError("");
    const res = await fetch(`/api/user/webinars/${webinar.id}/select-method`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        method: 'paid'
      })
    }).then(r => r.json()).catch(() => ({
      success: false
    }));
    if (res.success) {
      // Webinar = produk murah → default QRIS; user bisa ganti metode di halaman checkout
      const orderRes = await checkoutApi.createPayment({
        order_type: 'webinar_registration',
        product_id: webinar.id,
        payment_method: 'qris',
        ...(needsMobile ? {
          mobile: mobile.trim()
        } : {}),
        ...(webinarBump && webinarBumpSelected ? {
          addons: [{
            type: 'funnel_bump',
            step_id: webinarBump.step_id
          }]
        } : {})
      }, idempotencyKey);
      setSubmitting(false);
      const orderData = orderRes.data as {
        order_id?: string;
      } | undefined;
      if (orderRes.success && orderData?.order_id) {
        router.push(`/checkout/${orderData.order_id}`);
      } else {
        setError(orderRes.error || 'Gagal membuat tagihan pembayaran.');
      }
    } else {
      setSubmitting(false);
      setError(res.error || 'Gagal memilih pendaftaran berbayar.');
    }
  };

  // Bukti share DASAR (checklist "share ke N grup" bawaan, bukan kewajiban
  // custom admin) — sama pola compress+upload seperti handleObligationUpload,
  // tapi target endpoint terpisah (obligation_id NULL, lihat migration 1017).
  const handleShareProofUpload = async (file: File) => {
    if (!webinar) return;
    setShareProofBusy(true);
    setShareProofError("");
    try {
      const imageCompression = (await import("browser-image-compression")).default;
      const compressed = await imageCompression(file, {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 1280,
        useWebWorker: true
      });
      const formData = new FormData();
      formData.append("file", compressed, file.name);
      const res = await fetch(`/api/user/webinars/${webinar.id}/share-proof/upload`, {
        method: "POST",
        credentials: "include",
        body: formData
      }).then(r => r.json()).catch(() => ({
        success: false
      }));
      if (res.success) {
        await loadWebinar();
      } else {
        setShareProofError(res.error || "Gagal mengunggah bukti. Coba lagi.");
      }
    } catch {
      setShareProofError("Gagal memproses gambar. Coba file lain.");
    } finally {
      setShareProofBusy(false);
    }
  };

  // Kewajiban tambahan (opsional, admin-configurable) — lihat WebinarObligation.
  // Upload di-compress dulu di browser (screenshot HP bisa 5-10MB) sebelum
  // dikirim, supaya hemat kuota peserta & storage R2.
  const handleObligationUpload = async (obligation: WebinarObligation, file: File) => {
    if (!webinar) return;
    setObligationBusy(obligation.id);
    setObligationErrors(prev => ({
      ...prev,
      [obligation.id]: ""
    }));
    try {
      const imageCompression = (await import("browser-image-compression")).default;
      const compressed = await imageCompression(file, {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 1280,
        useWebWorker: true
      });
      const formData = new FormData();
      formData.append("file", compressed, file.name);
      const res = await fetch(`/api/user/webinars/${webinar.id}/obligations/${obligation.id}/upload`, {
        method: "POST",
        credentials: "include",
        body: formData
      }).then(r => r.json()).catch(() => ({
        success: false
      }));
      if (res.success) {
        await loadWebinar();
      } else {
        setObligationErrors(prev => ({
          ...prev,
          [obligation.id]: res.error || "Gagal mengunggah bukti. Coba lagi."
        }));
      }
    } catch {
      setObligationErrors(prev => ({
        ...prev,
        [obligation.id]: "Gagal memproses gambar. Coba file lain."
      }));
    } finally {
      setObligationBusy(null);
    }
  };
  const handleObligationComplete = async (obligation: WebinarObligation) => {
    if (!webinar) return;
    setObligationBusy(obligation.id);
    setObligationErrors(prev => ({
      ...prev,
      [obligation.id]: ""
    }));
    const res = await fetch(`/api/user/webinars/${webinar.id}/obligations/${obligation.id}/complete`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({})
    }).then(r => r.json()).catch(() => ({
      success: false
    }));
    setObligationBusy(null);
    if (res.success) {
      await loadWebinar();
    } else {
      setObligationErrors(prev => ({
        ...prev,
        [obligation.id]: res.error || "Gagal menandai kewajiban selesai."
      }));
    }
  };

  // download attribute browser sering diam-diam diabaikan (khusus in-app
  // browser Android/target="_blank") dan cuma buka tab baru menampilkan
  // gambar — bukan benar-benar mengunduh. Fetch manual jadi blob supaya
  // download SELALU terjadi terlepas dari perilaku browser.
  const handleDownloadFlyer = async () => {
    if (!webinar?.thumbnail_url) return;
    setDownloadingFlyer(true);
    try {
      const res = await fetch(webinar.thumbnail_url);
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const ext = webinar.thumbnail_url.split(".").pop()?.split(/[?#]/)[0] || "jpg";
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `flyer-${webinar.slug}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(webinar.thumbnail_url, "_blank", "noopener,noreferrer");
    } finally {
      setDownloadingFlyer(false);
    }
  };
  const handleConfirmShare = async () => {
    if (!webinar || !shareProofDone || !allObligationsDone || !integrityConfirmed) return;
    setSubmitting(true);
    setError("");
    const res = await fetch(`/api/user/webinars/${webinar.id}/confirm-free-share`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        confirmed: true,
        integrity_confirmed: true
      })
    }).then(r => r.json()).catch(() => ({
      success: false
    }));
    setSubmitting(false);
    if (res.success) {
      if (webinar.redirect_url) {
        window.location.href = webinar.redirect_url;
        return;
      }
      setSuccessMessage("Registrasi gratis berhasil dikonfirmasi. Email detail webinar akan segera dikirim.");
      loadWebinar();
    } else {
      setError(res.error || 'Gagal mengonfirmasi share.');
    }
  };
  const handleCopyMessage = async () => {
    if (shareData?.message) {
      await navigator.clipboard.writeText(shareData.message);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  // Registrasi sebagai efek samping daftar webinar — TIDAK butuh OTP (beda dari
  // signup langsung di /register). Akun langsung terverifikasi + login lewat
  // /api/auth/register-guest, jadi flow bisa lanjut di halaman yang sama tanpa
  // navigasi apa pun. handleContinue yang memutuskan langkah berikutnya.
  const handleGuestRegister = async (): Promise<boolean> => {
    if (!webinar) return false;
    const {
      name,
      email,
      phone,
      password
    } = guestForm;
    if (!name.trim() || !email.trim() || !phone.trim() || !password.trim()) {
      setError("Semua field wajib diisi.");
      return false;
    }
    if (password.length < 8) {
      setError("Password minimal 8 karakter.");
      return false;
    }
    if (password !== confirmPassword) {
      setError("Password tidak cocok.");
      return false;
    }
    setSubmitting(true);
    setError("");
    setIsDuplicate(false);
    setDuplicateField(null);
    const regResponse = await fetch("/api/auth/register-guest", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        password,
        referral_source: guestForm.referral_source || undefined,
        affiliate_code: document.cookie.split(';').find(c => c.trim().startsWith('affiliate_code='))?.split('=')[1]?.trim() || undefined,
        cfTurnstileResponse: turnstile.token
      })
    });
    const regRes = await regResponse.json().catch(() => ({
      success: false
    }));
    if (!regRes.success) {
      // Token Turnstile SEKALI PAKAI dan sudah dikonsumsi server sebelum cek
      // duplikat — tanpa reset, submit ulang setelah 409 pasti ditolak.
      if (isTurnstileError(regRes.errorType)) {
        turnstile.noteServerRejection(regRes.errorType);
        setError(regRes.error);
      } else {
        turnstile.reset();
        if (regResponse.status === 409) {
          setIsDuplicate(true);
          setDuplicateField((regRes.error as string)?.includes("WhatsApp") ? "phone" : "email");
        } else {
          setError(regRes.error || "Gagal membuat akun. Silakan coba lagi.");
        }
      }
      setSubmitting(false);
      return false;
    }
    const currentUser = regRes.data?.user;
    setIsLoggedIn(true);
    setUser({
      name: currentUser?.name || name.trim(),
      email: currentUser?.email || email.trim(),
      phone: currentUser?.phone || phone.trim()
    });
    setSubmitting(false);
    return true;
  };

  // Hanya melakukan autentikasi — TIDAK mengeksekusi select-method/registrasi
  // apa pun sendiri. handleContinue yang memutuskan langkah berikutnya setelah
  // login sukses (satu titik kontrol, bukan tersebar).
  const handleGuestLogin = async (): Promise<boolean> => {
    if (!webinar) return false;
    const {
      email,
      password
    } = loginForm;
    if (!email.trim() || !password.trim()) {
      setError("Email dan password wajib diisi.");
      return false;
    }
    setSubmitting(true);
    setError("");
    setIsDuplicate(false);
    setDuplicateField(null);
    const loginResponse = await fetch("/api/auth/login", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        email: email.trim(),
        password
      })
    });
    const loginRes = await loginResponse.json().catch(() => ({
      success: false
    }));
    if (!loginRes.success) {
      setError(loginRes.error || "Login gagal. Cek email dan password kamu.");
      setSubmitting(false);
      return false;
    }
    const currentUser = loginRes.data?.user || loginRes.data;
    setIsLoggedIn(true);
    setUser({
      name: currentUser.name || "",
      email: currentUser.email || email.trim(),
      phone: currentUser.phone || null
    });
    await loadWebinar();
    return true;
  };

  // Satu titik kontrol di balik tombol "Lanjutkan" — baru di sini eksekusi
  // sungguhan terjadi (select-method / checkout / registrasi lama). Klik kartu
  // pilihan di Page 1 TIDAK memanggil fungsi ini, cuma setSelectedMethod.
  const handleContinue = async () => {
    if (!webinar) return;
    if (isTwoStep && !selectedMethod) {
      setError("Pilih salah satu cara daftar dulu.");
      return;
    }
    if (!isLoggedIn) {
      if (authFormMode === "login") {
        const ok = await handleGuestLogin();
        if (!ok) return;
      } else {
        const ok = await handleGuestRegister();
        if (!ok) return;
      }
    }
    if (isPaidOnly) {
      await handleSelectPaid();
      return;
    }
    // Webinar gratis-saja (tanpa opsi bayar) TETAP wajib lewat layar share +
    // upload bukti — tidak ada jalur "langsung terdaftar" tanpa syarat.
    if (!webinar.paid_registration_enabled) {
      await handleSelectFreeShare();
      return;
    }
    if (selectedMethod === "paid") {
      await handleSelectPaid();
    } else if (selectedMethod === "free_share") {
      await handleSelectFreeShare();
    }
  };
  if (loading || isLoggedIn === null) {
    return <main id="main-content" tabIndex={-1} className="min-h-dvh bg-warm-cream flex items-center justify-center outline-none">
        <div role="status" className="text-sm text-[rgba(12,12,10,0.72)]">Memuat webinar...</div>
      </main>;
  }
  if (!webinar) {
    return <main id="main-content" tabIndex={-1} className="min-h-dvh bg-warm-cream flex items-center justify-center p-4 outline-none">
        <div className="max-w-md rounded-2xl border border-black/[0.08] bg-white p-6 text-center shadow-sm">
          <p className="mb-2 font-semibold text-warm-black">Webinar tidak ditemukan</p>
          <p className="mb-5 text-sm text-[rgba(12,12,10,0.65)]">{error || "Link webinar ini belum tersedia."}</p>
          <Link href="/" className="text-sm font-semibold text-primary">
            Lihat katalog webinar
          </Link>
        </div>
      </main>;
  }

  // Page 2 (jalur gratis) — tampilan penuh terpisah, BUKAN modal, mengikuti
  // pola growwithai-app yang diriset: seluruh isi Page 1 diganti (bukan
  // ditumpuk) selama proses share+kewajiban belum dikonfirmasi.
  if (!isCompleted && shareView) {
    return <main id="main-content" tabIndex={-1} className="min-h-dvh bg-warm-cream flex items-center justify-center p-4 outline-none" style={{
      backgroundImage: "linear-gradient(to right, rgba(12,12,10,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(12,12,10,0.04) 1px, transparent 1px)",
      backgroundSize: "40px 40px"
    }}>
        <div className="w-full max-w-md space-y-3">
          <div className="overflow-hidden rounded-2xl border border-[rgba(12,12,10,0.08)] bg-white shadow-sm">
            <div className="h-1 w-full bg-primary" />
            <div className="flex items-center justify-between px-6 pb-2 pt-6">
              <button type="button" onClick={() => setShareView(false)} className="flex items-center gap-1 text-xs font-semibold text-[rgba(12,12,10,0.65)] transition-colors hover:text-warm-black">
                ← Kembali
              </button>
              <BrandLogo width={96} height={24} className="h-6 w-auto" />
            </div>

            <div className="space-y-4 px-6 pb-6 pt-2">
              <div>
                <h1 className="text-lg font-semibold text-warm-black">Satu Langkah Lagi 🚀</h1>
                <p className="mt-1 text-xs text-[rgba(12,12,10,0.65)]">{webinar.title}</p>
              </div>

              <div className="space-y-3">
                <div className="space-y-3 rounded-xl border border-black/[0.08] bg-white p-4">
                  <div>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-[rgba(12,12,10,0.5)]">Pesan untuk dibagikan</span>
                    <p className="mt-1 text-xs text-[rgba(12,12,10,0.65)]">
                      Bagikan pesan ini ke {webinar.share_required_count || 2} grup WhatsApp/Telegram kamu:
                    </p>
                  </div>

                  {webinar.thumbnail_url &&
                // eslint-disable-next-line @next/next/no-img-element
                <img src={webinar.thumbnail_url} alt={`Flyer ${webinar.title}`} className="max-h-40 w-auto rounded-lg border border-black/[0.08] object-contain" />}

                  <div role="status" className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-lg border border-black/[0.08] bg-black/[0.02] p-3 font-mono text-xs text-[rgba(12,12,10,0.72)]">
                    {shareData?.message || "Memuat pesan..."}
                  </div>

                  <div className="flex items-center gap-2">
                    {webinar.thumbnail_url && <button type="button" onClick={handleDownloadFlyer} disabled={downloadingFlyer} className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-black/[0.08] bg-black/[0.02] py-2 text-xs font-semibold text-warm-black transition-colors hover:bg-black/[0.05] disabled:cursor-not-allowed disabled:opacity-50">
                        <Download className="h-3.5 w-3.5" /> {downloadingFlyer ? "Mengunduh..." : "Unduh Flyer"}
                      </button>}
                    <button type="button" onClick={handleCopyMessage} className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-black/[0.08] bg-black/[0.02] py-2 text-xs font-semibold text-warm-black transition-colors hover:bg-black/[0.05]">
                      {copySuccess ? <>
                          <Check className="h-3.5 w-3.5" /> Tersalin
                        </> : <>
                          <Copy className="h-3.5 w-3.5" /> Salin Pesan
                        </>}
                    </button>
                  </div>
                </div>

                <div>
                  <p className="px-1 text-xs font-semibold text-warm-black">
                    Unggah bukti share ({shareProofCount}/{shareProofRequired} terunggah)
                  </p>
                  <p className="px-1 text-xs text-[rgba(12,12,10,0.6)]">
                    Screenshot pesan di atas setelah dikirim ke tiap grup — satu foto per grup.
                  </p>
                </div>
                <ProofUploadSlots required={shareProofRequired} uploadedCount={shareProofCount} busy={shareProofBusy} onUpload={handleShareProofUpload} itemLabel="Grup" />
                {shareProofError && <p className="flex items-center gap-1 text-xs font-semibold text-red-600">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {shareProofError}
                  </p>}
                {shareProofDone && <p className="flex items-center gap-1 text-xs font-semibold text-green-700">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Bukti lengkap
                  </p>}

                {(webinar.obligations || []).map(ob => <div key={ob.id} className="rounded-xl border border-black/[0.08] bg-white p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-warm-black">{ob.label}</p>
                        {ob.action_url && <a href={ob.action_url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-primary hover:underline">
                            Buka tautan <ExternalLink className="inline h-3 w-3" />
                          </a>}
                      </div>
                      {ob.completed && <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" />}
                    </div>

                    {!ob.completed ? <div className="mt-2 space-y-2">
                        <p className="text-xs text-[rgba(12,12,10,0.6)]">
                          Unggah {ob.proof_count_required || 1} bukti ({ob.proof_count || 0}/{ob.proof_count_required || 1} terunggah)
                        </p>
                        <ProofUploadSlots required={ob.proof_count_required || 1} uploadedCount={ob.proof_count || 0} busy={obligationBusy === ob.id} onUpload={file => handleObligationUpload(ob, file)} itemLabel="Bukti" />
                        <button type="button" onClick={() => handleObligationComplete(ob)} disabled={obligationBusy === ob.id || (ob.proof_count || 0) < (ob.proof_count_required || 1)} className="w-full rounded-lg border border-black/[0.08] bg-black/[0.02] py-2 text-xs font-semibold text-warm-black transition-colors hover:bg-black/[0.05] disabled:cursor-not-allowed disabled:opacity-50">
                          {obligationBusy === ob.id ? "Memproses..." : "Tandai Selesai"}
                        </button>
                      </div> : null}
                    {obligationErrors[ob.id] && <p className="mt-2 flex items-center gap-1 text-xs font-semibold text-red-600">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {obligationErrors[ob.id]}
                      </p>}
                  </div>)}

                {!submitting && pendingFreeRegistrationReasons.length > 0 && <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <div>
                      <p className="font-semibold">Sebelum bisa disubmit, selesaikan dulu:</p>
                      <ul className="mt-1 list-disc space-y-0.5 pl-4">
                        {pendingFreeRegistrationReasons.map((reason, i) => <li key={i}>{reason}</li>)}
                      </ul>
                    </div>
                  </div>}

                {shareProofDone && allObligationsDone && <label className="flex items-start gap-2.5 rounded-lg border border-black/[0.08] bg-black/[0.02] px-3 py-2.5 text-xs text-warm-black">
                    <input type="checkbox" checked={integrityConfirmed} onChange={e => setIntegrityConfirmed(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-primary" />
                    <span>
                      Saya menyatakan bukti yang saya unggah asli, bukan hasil edit/rekayasa, dan sesuai
                      keadaan sebenarnya. Saya paham jika terbukti memalsukan bukti, akun saya bisa
                      diblokir permanen (blacklist) dari platform ini.
                    </span>
                  </label>}

                <button type="button" onClick={handleConfirmShare} disabled={!shareProofDone || !allObligationsDone || !integrityConfirmed || submitting} className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-[var(--brand-on-primary,#fff)] transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50">
                  {submitting ? "Memproses..." : "Selesaikan Pendaftaran Gratis"}
                </button>
              </div>

              {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>}
            </div>
          </div>
        </div>
      </main>;
  }
  return <main id="main-content" tabIndex={-1} className="min-h-dvh bg-warm-cream flex items-center justify-center p-4 outline-none" style={{
    backgroundImage: "linear-gradient(to right, rgba(12,12,10,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(12,12,10,0.04) 1px, transparent 1px)",
    backgroundSize: "40px 40px"
  }}>
      <div className="w-full max-w-md space-y-3">
        <div className="overflow-hidden rounded-2xl border border-[rgba(12,12,10,0.08)] bg-white shadow-sm">
          <div className="h-1 w-full bg-primary" />
          <div className="flex justify-center px-6 pb-5 pt-6">
            <BrandLogo width={128} height={32} priority className="h-8 w-auto" />
          </div>

          {webinar.thumbnail_url &&
        // eslint-disable-next-line @next/next/no-img-element
        <img src={webinar.thumbnail_url} alt={webinar.title} fetchPriority="high" decoding="async" className="block h-auto w-full bg-black/[0.03]" />}

          <div className="border-t border-black/[0.06] px-6 py-4">
            <div className="mb-3 flex items-center gap-2">
              <Ticket className="h-4 w-4 text-[rgba(12,12,10,0.4)]" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-[rgba(12,12,10,0.72)]">
                Webinar {webinar.paid_registration_enabled ? "Eksklusif" : "Gratis"}
              </span>
            </div>
            <h1 className="text-lg font-semibold leading-tight text-warm-black">{webinar.title}</h1>
            {webinar.subtitle && <p className="mt-1 text-xs leading-relaxed text-[rgba(12,12,10,0.65)]">{webinar.subtitle}</p>}
            {!webinar.paid_registration_enabled && <div className="mt-3 rounded-xl border border-green-200 bg-green-50 p-3">
                <p className="text-sm font-semibold text-green-700">Pendaftaran gratis 100%</p>
                <p className="mt-0.5 text-xs text-green-800">
                  {hasStarted ? "Event sudah berjalan. Login untuk akses Zoom atau rekaman yang tersedia." : "Daftar gratis dengan share ke beberapa grup untuk mengamankan slot."}
                </p>
              </div>}
          </div>

          <div className="border-t border-black/[0.06] px-6 py-4">
            <div className="mb-3 flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-[rgba(12,12,10,0.4)]" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-[rgba(12,12,10,0.72)]">
                Jadwal
              </span>
            </div>
            <p className="text-sm font-semibold text-warm-black">
              {formatEventDate(webinar.starts_at, webinar.timezone || "Asia/Jakarta")}
            </p>
            {webinar.host_name && <p className="mt-1 text-xs text-[rgba(12,12,10,0.68)]">Host: {webinar.host_name}</p>}
            {typeof webinar.registered_count === "number" && <div className="mt-1 flex items-center gap-2 text-xs text-[rgba(12,12,10,0.68)]">
                {webinar.participants_preview && webinar.participants_preview.length > 0 ? <AvatarStack participants={webinar.participants_preview} total={webinar.registered_count} /> : <span>{webinar.registered_count}{webinar.capacity ? `/${webinar.capacity}` : ""} pendaftar</span>}
              </div>}
            {!hasStarted && timeRemaining && <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3">
                <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-amber-800">
                  <Timer className="h-4 w-4" />
                  Pendaftaran berakhir saat event dimulai
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {[{
                label: "Hari",
                value: timeRemaining.days
              }, {
                label: "Jam",
                value: timeRemaining.hours
              }, {
                label: "Menit",
                value: timeRemaining.minutes
              }, {
                label: "Detik",
                value: timeRemaining.seconds
              }].map(item => <div key={item.label} className="min-w-0 rounded-lg bg-white px-2 py-2 text-center ring-1 ring-amber-200">
                      <p className="text-lg font-bold tabular-nums text-warm-black">{String(item.value).padStart(2, "0")}</p>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-[rgba(12,12,10,0.72)]">{item.label}</p>
                    </div>)}
                </div>
              </div>}
          </div>

          {!isCompleted && <div className="border-t border-black/[0.06] px-6 py-4">
              <div className="mb-3 flex items-center gap-2">
                <User className="h-4 w-4 text-[rgba(12,12,10,0.4)]" />
                <span className="text-[10px] font-semibold uppercase tracking-widest text-[rgba(12,12,10,0.72)]">
                  {isLoggedIn ? "Data Peserta" : authFormMode === "login" ? "Masuk Akun" : "Buat Akun Baru"}
                </span>
              </div>

              {isLoggedIn && user ? <div className="space-y-3">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-[rgba(12,12,10,0.65)]">Nama</label>
                    <input readOnly value={user.name} className={readOnlyInputClass} />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-[rgba(12,12,10,0.65)]">Email</label>
                    <input readOnly value={user.email} className={readOnlyInputClass} />
                  </div>
                  {user.phone ? <div>
                      <label className="mb-1.5 block text-xs font-semibold text-[rgba(12,12,10,0.65)]">WhatsApp</label>
                      <input readOnly value={user.phone} className={readOnlyInputClass} />
                    </div> : needsMobile ? <div>
                      <label className="mb-1.5 block text-xs font-semibold text-[rgba(12,12,10,0.65)]">
                        WhatsApp <span className="text-red-500">*</span>
                      </label>
                      <input type="tel" name="mobile" autoComplete="tel" value={mobile} onChange={e => setMobile(e.target.value)} placeholder="08xxxxxxxxxx" className={inputClass} />
                      <p className="mt-1 text-xs text-[rgba(12,12,10,0.65)]">Digunakan untuk konfirmasi pembayaran</p>
                    </div> : null}
                </div> : <div className="space-y-3">
                  {authFormMode === "register" ? <>
                      <p className="text-xs leading-5 text-[rgba(12,12,10,0.65)]">
                        Isi data di bawah. Akun dibuat otomatis sebelum melanjutkan.{" "}
                        <button type="button" onClick={() => {
                  setAuthFormMode("login");
                  setError("");
                  setIsDuplicate(false);
                  setDuplicateField(null);
                  setLoginForm(prev => ({
                    ...prev,
                    email: guestForm.email || prev.email
                  }));
                }} className="font-semibold text-primary hover:underline">
                          Sudah punya akun?
                        </button>
                      </p>
                      {([{
                key: "name",
                label: "Nama Lengkap",
                type: "text",
                placeholder: "John Doe",
                autoComplete: "name"
              }, {
                key: "email",
                label: "Email",
                type: "email",
                placeholder: "nama@email.com",
                autoComplete: "email"
              }, {
                key: "phone",
                label: "WhatsApp",
                type: "tel",
                placeholder: "08xxxxxxxxxx",
                autoComplete: "tel"
              }] as const).map(field => <div key={field.key}>
                          <label className="mb-1.5 block text-xs font-semibold text-[rgba(12,12,10,0.65)]">
                            {field.label} <span className="text-red-500">*</span>
                          </label>
                          <input type={field.type} name={field.key} autoComplete={field.autoComplete} value={guestForm[field.key]} onChange={event => {
                  setGuestForm(prev => ({
                    ...prev,
                    [field.key]: event.target.value
                  }));
                  if (isDuplicate) {
                    setIsDuplicate(false);
                    setDuplicateField(null);
                  }
                }} placeholder={field.placeholder} className={inputClass} />
                        </div>)}
                      <div>
                        <label htmlFor="webinar-referral-source" className="mb-1.5 block text-xs font-semibold text-[rgba(12,12,10,0.65)]">
                          Tau {brand_name} dari mana?
                        </label>
                        <select id="webinar-referral-source" value={guestForm.referral_source} onChange={e => setGuestForm(prev => ({
                  ...prev,
                  referral_source: e.target.value
                }))} className={inputClass}>
                          <option value="">-- Pilih --</option>
                          <option value="Instagram">Instagram</option>
                          <option value="TikTok">TikTok</option>
                          <option value="YouTube">YouTube</option>
                          <option value="Google / Search">Google / Search</option>
                          <option value="Dari teman">Dari teman</option>
                          <option value="Komunitas WhatsApp/Telegram">Komunitas WhatsApp / Telegram</option>
                          <option value="Lainnya">Lainnya</option>
                        </select>
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold text-[rgba(12,12,10,0.65)]">
                          Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input type={showPassword ? "text" : "password"} name="new-password" autoComplete="new-password" value={guestForm.password} onChange={event => setGuestForm(prev => ({
                    ...prev,
                    password: event.target.value
                  }))} placeholder="Minimal 8 karakter" className={`${inputClass} pr-10`} />
                          <button type="button" onClick={() => setShowPassword(prev => !prev)} className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 text-[rgba(12,12,10,0.4)] transition-colors hover:text-warm-black" aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"}>
                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold text-[rgba(12,12,10,0.65)]">
                          Ulangi Password <span className="text-red-500">*</span>
                        </label>
                        <input type={showPassword ? "text" : "password"} name="confirm-password" autoComplete="new-password" value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} placeholder="Ulangi password" className={inputClass} />
                        {confirmPassword && guestForm.password !== confirmPassword && <p className="mt-1 text-xs text-red-500">Password tidak cocok</p>}
                      </div>
                      <input type="text" name="_hp_website" value={guestForm._hp_website} onChange={e => setGuestForm(prev => ({
                ...prev,
                _hp_website: e.target.value
              }))} style={{
                display: 'none'
              }} tabIndex={-1} autoComplete="off" aria-hidden="true" />
                      {turnstile.widget}
                    </> : <>
                      <p className="text-xs leading-5 text-[rgba(12,12,10,0.65)]">
                        Masuk dengan akun {brand_name} kamu.{" "}
                        <button type="button" onClick={() => {
                  setAuthFormMode("register");
                  setError("");
                }} className="font-semibold text-primary hover:underline">
                          Belum punya akun?
                        </button>
                      </p>
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold text-[rgba(12,12,10,0.65)]">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input type="email" name="email" autoComplete="email" value={loginForm.email} onChange={event => setLoginForm(prev => ({
                  ...prev,
                  email: event.target.value
                }))} placeholder="nama@email.com" className={inputClass} />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold text-[rgba(12,12,10,0.65)]">
                          Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input type={showPassword ? "text" : "password"} name="current-password" autoComplete="current-password" value={loginForm.password} onChange={event => setLoginForm(prev => ({
                    ...prev,
                    password: event.target.value
                  }))} placeholder="Password akun kamu" className={`${inputClass} pr-10`} />
                          <button type="button" onClick={() => setShowPassword(prev => !prev)} className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 text-[rgba(12,12,10,0.4)] transition-colors hover:text-warm-black" aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"}>
                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                        <Link href="/forgot-password" className="mt-1.5 inline-block text-xs font-semibold text-primary hover:underline">
                          Lupa password?
                        </Link>
                      </div>
                    </>}
                </div>}
            </div>}

          <div className="border-t border-black/[0.06] px-6 py-4">
            <div className="mb-3 flex items-center gap-2">
              <Receipt className="h-4 w-4 text-[rgba(12,12,10,0.4)]" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-[rgba(12,12,10,0.72)]">
                {isTwoStep ? "Pilih Cara Daftar" : "Ringkasan Harga"}
              </span>
            </div>

            {isPaidOnly ? <div className="mb-3 space-y-2 text-sm">
                {hasOriginalPrice && <div className="flex items-center justify-between gap-3 text-[rgba(12,12,10,0.6)]">
                    <span>Harga Sebenarnya</span>
                    <span className="font-semibold line-through text-[rgba(12,12,10,0.4)]">{formatRupiah(webinar.original_price!)}</span>
                  </div>}
                <div className="flex items-center justify-between gap-3 border-t border-black/[0.06] pt-2">
                  <span className="font-semibold text-warm-black">Total</span>
                  <span className="font-bold text-warm-black">{formatRupiah(webinar.paid_registration_price || 49000)}</span>
                </div>
              </div> : isTwoStep && !isCompleted ? <div className="mb-1 space-y-3">
                {hasOriginalPrice && <div className="flex items-center justify-between gap-3 text-sm text-[rgba(12,12,10,0.6)]">
                    <span>Harga Sebenarnya</span>
                    <span className="font-semibold line-through text-[rgba(12,12,10,0.4)]">{formatRupiah(webinar.original_price!)}</span>
                  </div>}
                <p className="text-xs text-[rgba(12,12,10,0.65)]">Bebas pilih — bisa diganti kapan saja sebelum klik Lanjutkan di bawah.</p>

                {webinar.free_registration_enabled && <button type="button" onClick={() => setSelectedMethod("free_share")} className={`relative w-full rounded-xl border-2 p-4 text-left transition-all ${selectedMethod === "free_share" ? "border-primary bg-[var(--brand-primary-soft)]" : "border-black/[0.08] bg-white hover:bg-black/[0.02]"}`}>
                    {selectedMethod === "free_share" && <span className="absolute right-4 top-4 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[var(--brand-on-primary,#fff)]">
                        Dipilih
                      </span>}
                    <h3 className="mb-1 text-base font-bold text-warm-black">Opsi 1: Gratis 100%</h3>
                    <ul className="mt-2 space-y-1 text-sm text-[rgba(12,12,10,0.65)]">
                      <li>
                        • Share ke{" "}
                        <span className="font-semibold text-warm-black">{webinar.share_required_count || 2} Grup WhatsApp/Telegram</span>
                      </li>
                      {(webinar.obligations || []).map(ob => <li key={ob.id}>
                          • {ob.label} (perlu bukti upload)
                        </li>)}
                    </ul>
                  </button>}

                <button type="button" onClick={() => setSelectedMethod("paid")} className={`relative w-full rounded-xl border-2 p-4 text-left transition-all ${selectedMethod === "paid" ? "border-primary bg-[var(--brand-primary-soft)]" : "border-black/[0.08] bg-white hover:bg-black/[0.02]"}`}>
                  {selectedMethod === "paid" && <span className="absolute right-4 top-4 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[var(--brand-on-primary,#fff)]">
                      Dipilih
                    </span>}
                  <h3 className="mb-1 text-base font-bold text-warm-black">Opsi 2: Bayar Langsung</h3>
                  <ul className="mt-2 space-y-1 text-sm text-[rgba(12,12,10,0.65)]">
                    <li>
                      •{" "}
                      <span className="font-semibold text-warm-black">
                        {formatRupiah(webinar.paid_registration_price || 49000)}
                        {webinarBump && webinarBumpSelected ? ` + ${formatRupiah(webinarBump.amount)}` : ""}
                      </span>
                    </li>
                    <li>• Langsung akses, tanpa syarat tambahan</li>
                  </ul>
                </button>

                {webinarBump && selectedMethod !== "free_share" && <FunnelBumpCard bump={webinarBump} selected={webinarBumpSelected} onChange={setWebinarBumpSelected} />}
              </div> : webinar.paid_registration_enabled ? <div className="mb-3 space-y-2 text-sm">
                {hasOriginalPrice && <div className="flex items-center justify-between gap-3 text-[rgba(12,12,10,0.6)]">
                    <span>Harga Sebenarnya</span>
                    <span className="font-semibold line-through text-[rgba(12,12,10,0.4)]">{formatRupiah(webinar.original_price!)}</span>
                  </div>}
                <div className="flex items-center justify-between gap-3 border-t border-black/[0.06] pt-2">
                  <span className="font-semibold text-warm-black">Opsi 1: Gratis</span>
                  <span className="font-bold text-green-700">Share ke {webinar.share_required_count || 2} Grup</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="font-semibold text-warm-black">Opsi 2: Bayar Langsung</span>
                  <span className="font-bold text-warm-black">{formatRupiah(webinar.paid_registration_price || 49000)}</span>
                </div>
                <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2 mt-2 border border-amber-200">
                  <AlertCircle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                  Kamu dapat mendaftar gratis dengan membantu membagikan info webinar ini.
                </p>
              </div> : <div className="mb-3 flex items-center justify-between gap-3 border-t border-black/[0.06] pt-2 text-sm">
                <span className="font-semibold text-warm-black">Total</span>
                <span className="font-bold text-green-700">Gratis</span>
              </div>}
          </div>

          {isDuplicate && !isLoggedIn && <div className="mx-5 mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3.5">
              <p className="mb-0.5 text-xs font-semibold text-amber-800">Akun sudah terdaftar</p>
              <p className="mb-2.5 text-[11px] text-amber-700">
                {duplicateField === "phone" ? "Nomor WhatsApp ini sudah digunakan oleh akun lain." : `Email ini sudah terdaftar di ${brand_name}.`}
              </p>
              <div className="flex gap-2">
                <button type="button" onClick={() => {
              setAuthFormMode("login");
              setError("");
              setIsDuplicate(false);
              setDuplicateField(null);
              setLoginForm(prev => ({
                ...prev,
                email: guestForm.email || prev.email
              }));
            }} className="flex-1 rounded-md bg-[var(--brand-primary)] py-1.5 text-center text-[11px] font-semibold text-[var(--brand-on-primary,#fff)] transition-colors hover:bg-[var(--brand-primary-hover)]">
                  Masuk
                </button>
                <Link href="/forgot-password" className="flex-1 rounded-md border border-[var(--brand-primary)]/30 py-1.5 text-center text-[11px] font-semibold text-[var(--brand-primary)] transition-colors hover:bg-[var(--brand-primary-soft)]">
                  Lupa Kata Sandi
                </Link>
              </div>
            </div>}

          {error && <div className="mx-5 mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>}
          {successMessage && <div className="mx-5 mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              {successMessage}
            </div>}
        </div>

        <div className="rounded-2xl border border-[rgba(12,12,10,0.08)] bg-white px-5 py-4 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="flex items-baseline gap-2">
                <p className={`text-xl font-bold ${isPaidOnly ? "text-warm-black" : "text-green-700"}`}>
                  {isPaidOnly ? formatRupiah(webinar.paid_registration_price || 49000) : "Gratis"}
                </p>
              </div>
              {webinar.paid_registration_enabled && !isPaidOnly && !isCompleted && !hasStarted && <p className="text-[11px] font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full inline-block mt-0.5">Hanya dengan share WA</p>}
            </div>
            {isCompleted || hasStarted ? webinar.join_url ? <a href={webinar.join_url} target="_blank" rel="noopener noreferrer" className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-[var(--brand-on-primary,#fff)] transition-colors hover:bg-primary-hover">
                  Akses Zoom
                  <ExternalLink className="h-4 w-4" />
                </a> : webinar.calendar_url ? <a href={webinar.calendar_url} target="_blank" rel="noopener noreferrer" className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-[var(--brand-on-primary,#fff)] transition-colors hover:bg-primary-hover">
                  <CalendarDays className="h-4 w-4" />
                  Simpan ke Kalender
                </a> : null : <button onClick={handleContinue} disabled={submitting || isTwoStep && !selectedMethod} className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-[var(--brand-on-primary,#fff)] transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60">
                {submitting ? <>
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Memproses...
                  </> : !isLoggedIn ? authFormMode === "login" ? "Masuk & Lanjutkan" : "Daftar & Lanjutkan" : isPaidOnly ? webinar.registration?.status === 'payment_pending' ? "Lanjut Bayar" : "Bayar Sekarang" : !webinar.paid_registration_enabled ? "Daftar Webinar" : "Lanjutkan"}
              </button>}
          </div>
          {/* Baris penuh terpisah, BUKAN disempitkan di sisi kanan flex row di
              atas — kalimat ini terlalu panjang untuk duduk sejajar dengan
              harga tanpa wrap yang menjorok aneh. */}
          {isCompleted && !webinar.join_url && !webinar.calendar_url && <p className="mt-3 flex items-start gap-1.5 text-xs font-semibold text-green-700">
              <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
              Kamu terdaftar — link Zoom akan muncul di sini menjelang acara
            </p>}
          {/* Belum terdaftar & acara sudah mulai: pendaftaran ditutup. Sebelumnya
              baris di atas ikut tampil hanya karena `hasStarted`, sehingga tamu
              yang tidak pernah mendaftar dibilang "Kamu terdaftar". */}
          {!isCompleted && hasStarted && <p className="mt-3 text-xs font-semibold text-[rgba(12,12,10,0.55)]">
              Pendaftaran sudah ditutup — acara ini sudah berjalan.
            </p>}
        </div>

        {canUpgradeToPaid && <div className="rounded-2xl border border-[rgba(12,12,10,0.08)] bg-white px-5 py-4 text-center shadow-sm">
            <p className="mb-2 text-xs text-[rgba(12,12,10,0.65)]">
              Ingin akses tanpa syarat share ke grup?
            </p>
            {needsMobile && <input type="tel" value={mobile} onChange={e => setMobile(e.target.value)} placeholder="08xxxxxxxxxx (WhatsApp untuk konfirmasi pembayaran)" className={`${inputClass} mb-2`} />}
            <button type="button" onClick={handleSelectPaid} disabled={submitting} className="text-sm font-semibold text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50">
              Upgrade ke akses berbayar ({formatRupiah(webinar.paid_registration_price || 49000)}) →
            </button>
          </div>}

        {webinar.body_html && <div className="rounded-2xl border border-[rgba(12,12,10,0.08)] bg-white p-5 shadow-sm">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-[rgba(12,12,10,0.72)]">
              Tentang Webinar
            </span>
            <div className={`relative mt-3 overflow-hidden ${contentExpanded ? "" : "max-h-40"}`}>
              <div className="rte-content text-[rgba(12,12,10,0.72)]" dangerouslySetInnerHTML={{
            __html: sanitizeLessonHtml(webinar.body_html)
          }} />
              {!contentExpanded && <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white to-transparent" />}
            </div>
            <button type="button" onClick={() => setContentExpanded(v => !v)} className="mt-2 flex w-full items-center justify-center gap-1 text-xs font-semibold text-primary hover:underline">
              {contentExpanded ? <>Tampilkan lebih sedikit <ChevronUp className="h-3.5 w-3.5" /></> : <>Baca Selengkapnya <ChevronDown className="h-3.5 w-3.5" /></>}
            </button>
          </div>}

        <p className="text-center text-sm text-[rgba(12,12,10,0.72)]">
          <Link href="/" className="transition-colors hover:text-primary">
            Lihat semua webinar
          </Link>
        </p>
      </div>
    </main>;
}